/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { FC } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import useSWRInfinite from 'swr/infinite';
import { Verified } from '@components/avatar';
import { Box, Button, Container, Step, Text } from '@components/design-system';
import { ThumbnailBoard } from '@components/thumbnail-board';
import { IBoard } from '@interface/board';
import { actions as globalActions } from '@state/global';
import { actions } from '@state/modals';
import { RootState } from '@state/store';
import { boardsFetcher } from '@utils/fetcher';
import { getSmallestImageBoardPreview } from '@utils/token';
import { useMedia } from '@utils/use-media';
import { getScreenName } from '@utils/user';
import * as Styles from './styles';

const Grid = dynamic(() => import('@components/grid'), { ssr: false });

interface ISmartGridBoards {
  api: string;
  query?: string;
  limit: number;
  allowNew?: boolean;
  canEdit?: boolean;
}

// same as SmartGridBoards but without contextual routing
export const SmartGridBoards: FC<ISmartGridBoards> = ({ api, query, limit, allowNew = false, canEdit = false }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const router = useRouter();
  const columns = useMedia(['(min-width: 1440px)', '(min-width: 640px)'], [5, 3], 2);
  const space = useMedia(['(min-width: 1440px)', '(min-width: 640px)'], ['32px', '16px'], '0px');
  const { data, error, size, setSize, mutate } = useSWRInfinite(
    (i: number, previousPageData: any) => {
      if (previousPageData && !previousPageData.length) return null;
      return `${api}${
        query ? `?${query}&limit=${limit}&offset=${(i + 0) * limit}` : `?limit=${limit}&offset=${(i + 0) * limit}`
      }`;
    },
    boardsFetcher,
    {
      revalidateIfStale: false,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
    }
  );

  const viewer = auth.user?.id;

  const items = data ? [].concat(...data) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data && data.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.length < limit);

  const handleAddNewCollection = () => {
    const callback = () => {
      dispatch({ type: globalActions.GLOBAL_GET_BOARDS, payload: auth.user });
      mutate();
    };
    dispatch({ type: actions.MODALS_SET_MODAL, payload: { type: 'board-create', callback } });
  };

  const handleEditBoard = (board: IBoard) => {
    const callback = () => {
      dispatch({ type: globalActions.GLOBAL_GET_BOARDS, payload: auth.user });
      mutate();
    };
    dispatch({ type: actions.MODALS_SET_MODAL, payload: { type: 'board-edit', board, callback } });
  };

  const renderThumbnail = (board: IBoard) => {
    const screenName = getScreenName(
      board.user.verified_on_chain,
      board.user.screen_name,
      board.user.id,
      board.user.name
    );
    const images = board.tokens.map(({ token }: any) => getSmallestImageBoardPreview(token)).map((e: any) => e.src);

    return (
      <div key={board.id} style={{ overflow: 'hidden', position: 'relative' }}>
        <Link
          key={`${board.id}-${board.private ? 'draft' : 'public'}`}
          href={board.private ? `/draft/${board.id}` : `/board/${board.id}`}
          locale={router.locale}
          prefetch={false}
          passHref
        >
          <a>
            <ThumbnailBoard key={board.id} cover={board.cover_uri} images={images} />
          </a>
        </Link>

        <Box css={{ flexDirection: 'column', padding: '$space$4' }}>
          {board.title && (
            <Text unset css={{ textAlign: 'left' }} ellipsis>
              <strong>{board.title}</strong>
            </Text>
          )}

          {!canEdit && (
            <Link href={`/user/${board.user.id}/created`} locale={router.locale} prefetch={false} passHref>
              <Button as="a" variant="basic" css={{ fontSize: 14, lineHeight: 1, display: 'flex' }}>
                {screenName}
                {board.user.verified_on_chain && <Verified size={10} secondary />}
              </Button>
            </Link>
          )}
        </Box>

        {canEdit && viewer === board.user.id && (
          <Box css={{ position: 'absolute', top: 8, left: 8 }}>
            <Button variant="tag" onClick={() => handleEditBoard(board)}>
              Edit
            </Button>
          </Box>
        )}
      </div>
    );
  };

  return (
    <>
      {!isLoadingInitialData && items.length === 0 && allowNew === false && (
        <Container>
          <Text unset css={{ color: '$secondaryFill' }}>
            No data to display
          </Text>
        </Container>
      )}

      <Grid columns={columns} space={space}>
        {allowNew && (
          <>
            <Styles.NewCollection onClick={handleAddNewCollection} css={{ marginBottom: 0 }}>
              <ThumbnailBoard icon={<i>add</i>} />
            </Styles.NewCollection>
            <Box css={{ flexDirection: 'column', padding: '$space$4' }}>
              <Text unset css={{ textAlign: 'left' }} ellipsis>
                <strong>New Board</strong>
              </Text>
            </Box>
          </>
        )}
        {items.map((board: IBoard) => renderThumbnail(board))}
      </Grid>

      {!isReachingEnd && (
        <Box css={{ padding: '$1 0' }} horizontal>
          {isLoadingMore ? (
            <Step progress />
          ) : (
            <Button onClick={() => setSize(size + 1)} variant="secondary">
              Load more
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
